import { InjectionToken, signal } from '@angular/core';
import { dsDtos, oofDtos } from '@vectronic/openApi-admin';
import { oofDtos as customerOofDtos } from '@vectronic/openApi-customer';
import { getCurrentTimespan } from '../helpers/timeline.helpers';

type NetworkStatus = dsDtos.NetworkStatusTimespan.DataEnum;

/**
 * Device record as returned by the admin device list (`idDevice` +
 * `networkStatusTimeline`) or the customer collar list (`id` + flat
 * `networkStatus`).
 */
export type DeviceWithNetworkStatus = {
  idDevice?: number;
  id?: number;
  networkStatusTimeline?: dsDtos.NetworkStatusTimespan[];
  networkStatus?: NetworkStatus;
};

export type RmaSide = oofDtos.AdminRma | customerOofDtos.CustomerRma;

/**
 * Reads the current network status of the devices of an RMA. The admin and the
 * customer side read from different device APIs, so each app-facing side gets
 * its own implementation, wired via `RMA_DEVICE_NETWORK_STATUS`.
 */
export abstract class RmaDeviceNetworkStatusService {
  private statuses = signal<Record<number, NetworkStatus>>({});

  networkStatusFor(deviceId: number | undefined): NetworkStatus | undefined {
    return deviceId == null ? undefined : this.statuses()[deviceId];
  }

  abstract loadForRma(rma: RmaSide): void;

  protected setStatuses(devices: DeviceWithNetworkStatus[]) {
    this.statuses.update((current) => ({ ...current, ...this.toStatusMap(devices) }));
  }

  private toStatusMap(devices: DeviceWithNetworkStatus[]) {
    return devices.reduce<Record<number, NetworkStatus>>((map, device) => {
      const status =
        device.networkStatus ?? getCurrentTimespan(device.networkStatusTimeline ?? [])?.data;
      const deviceId = device.idDevice ?? device.id;
      if (deviceId != null && status) map[deviceId] = status;
      return map;
    }, {});
  }
}

export const RMA_DEVICE_NETWORK_STATUS = new InjectionToken<RmaDeviceNetworkStatusService>(
  'RmaDeviceNetworkStatusService',
);

/**
 * Unique non-null device ids referenced by the items of an RMA, independent of
 * the admin or customer view.
 */
export function rmaDeviceIds(rma: RmaSide): number[] {
  const ids = ('items' in rma ? (rma.items ?? []) : []).map((item) =>
    'intake' in item
      ? item.intake?.identification.device?.deviceId
      : (item as customerOofDtos.CustomerRmaItem).deviceId,
  );
  return Array.from(new Set(ids.filter((id) => id != null)));
}
